import { useState } from "react";
import dayjs from "dayjs";
import { db } from "../../lib/api";
import { useQuery } from "../../lib/hooks";
import { PageHead } from "../../components/Shell";
import { ErrorBox, Loading, Panel, Seg, money, fmtShort } from "../../components/ui";
import { Columns, Delta, Kpi, LAST_YEAR, THIS_YEAR } from "../../components/charts";
import { METRIC_LABEL, MONTHS } from "./Dashboards";

const METRICS = ["outreach", "follow_ups", "appointments", "client_moves", "recruit_moves", "builder_moves", "touches", "two_way", "posts", "new_clients", "recruits", "premium", "points"];
const fmt = (k: string, v: number) => (k === "premium" ? money(v) : Number(v ?? 0).toLocaleString());

export default function Reports() {
  const years = Array.from({ length: dayjs().year() - 2021 }, (_, i) => dayjs().year() - i);
  const [year, setYear] = useState(dayjs().year());
  const [cmp, setCmp] = useState(dayjs().year() - 1);
  const [view, setView] = useState<"months" | "quarters" | "weeks">("months");
  const [metric, setMetric] = useState("touches");
  const q = useQuery(`dash:reports:${year}:${cmp}`, () => db.rpc<any>("dash_reports", { p_year: year, p_compare: cmp }));
  const d = q.data;

  const months: any[] = d?.months ?? [];
  const quarters = [1, 2, 3, 4].map((qq) => {
    const ms = months.slice((qq - 1) * 3, qq * 3);
    const sum = (rows: any[]) => Object.fromEntries(METRICS.map((k) => [k, rows.reduce((s, r) => s + Number(r?.[k] ?? 0), 0)]));
    return { label: `Q${qq}`, ...sum(ms), compare: sum(ms.map((m) => m.compare)) };
  });
  const rows: any[] = view === "months" ? months.map((m) => ({ ...m, label: MONTHS[m.month - 1] }))
    : view === "quarters" ? quarters
    : (d?.weeks ?? []).map((w: any) => ({ ...w, label: `${fmtShort(w.from)} – ${fmtShort(w.to)}` }));

  return (
    <>
      <PageHead title="Reports" sub="Year against year, month by month — like the Reports sheet. Pick any metric to chart it." />
      <div className="filters">
        <span className="lbl">Year</span>
        <select className="select" value={year} onChange={(e) => setYear(Number(e.target.value))}>{years.map((y) => <option key={y}>{y}</option>)}</select>
        <span className="lbl">compared with</span>
        <select className="select" value={cmp} onChange={(e) => setCmp(Number(e.target.value))}>{years.map((y) => <option key={y}>{y}</option>)}</select>
        <Seg value={view} onChange={setView} options={[{ value: "months", label: "Months" }, { value: "quarters", label: "Quarters" }, { value: "weeks", label: "Weeks" }]} />
      </div>
      <ErrorBox error={q.error} />
      {!d ? <Loading rows={8} /> : (
        <div className="stack">
          <div className="kpis">
            {["touches", "appointments", "new_clients", "recruits", "premium", "points"].map((k, i) => (
              <Kpi key={k} dark={i === 0} label={METRIC_LABEL[k]} value={fmt(k, d.year?.[k])} actual={Number(d.year?.[k] ?? 0)} prev={Number(d.compare?.[k] ?? 0)} />
            ))}
          </div>
          <Panel title={`${METRIC_LABEL[metric]} by month`} hint={`${year} against ${cmp}`}
            actions={<select className="select" style={{ width: "auto", borderRadius: 999 }} value={metric} onChange={(e) => setMetric(e.target.value)}>{METRICS.map((k) => <option key={k} value={k}>{METRIC_LABEL[k]}</option>)}</select>}>
            <div className="panel-body">
              <Columns labels={MONTHS} format={metric === "premium" ? (n) => (n >= 1000 ? `$${Math.round(n / 100) / 10}k` : `$${Math.round(n)}`) : undefined}
                series={[{ key: "c", label: String(cmp), color: LAST_YEAR, values: months.map((m) => Number(m.compare?.[metric] ?? 0)) }, { key: "y", label: String(year), color: THIS_YEAR, values: months.map((m) => Number(m[metric] ?? 0)) }]} />
            </div>
          </Panel>
          <Panel title={view === "weeks" ? `Week by week · ${year}` : view === "quarters" ? `Quarter by quarter · ${year}` : `Month by month · ${year}`}
            hint={view === "weeks" ? undefined : `Arrows compare with the same ${view === "months" ? "month" : "quarter"} in ${cmp}`}>
            <div className="table-wrap">
              <table className="data">
                <thead><tr><th>{view === "weeks" ? "Week" : view === "quarters" ? "Quarter" : "Month"}</th>{METRICS.map((k) => <th key={k} className="num">{METRIC_LABEL[k]}</th>)}</tr></thead>
                <tbody>
                  {rows.map((r, i) => {
                    if (view === "weeks" && dayjs(r.from).isAfter(dayjs())) return null;
                    return (
                      <tr key={i}>
                        <td className="nowrap"><b style={{ color: "var(--navy)" }}>{r.label}</b></td>
                        {METRICS.map((k) => (
                          <td key={k} className="num nowrap">
                            {fmt(k, r[k])}
                            {r.compare && <div><Delta now={Number(r[k] ?? 0)} prev={Number(r.compare[k] ?? 0)} suffix="" /></div>}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr><td>{year} total</td>{METRICS.map((k) => <td key={k} className="num">{fmt(k, d.year?.[k])}</td>)}</tr>
                  <tr><td className="muted">{cmp} total</td>{METRICS.map((k) => <td key={k} className="num muted">{fmt(k, d.compare?.[k])}</td>)}</tr></tfoot>
              </table>
            </div>
          </Panel>
        </div>
      )}
    </>
  );
}
